import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import InternalSidebar from '../../components/InternalSidebar';
import InternalTopMenu from '../../components/InternalTopMenu';
import EmailVerificationBanner from '../../components/EmailVerificationBanner';
import {
  ACCOUNT_LEVELS,
  getLevelById,
  resolveUserLevel,
  getRegistrationStep,
  getNextPendingLevel,
  isEmailVerified,
  MAX_ACCOUNT_LEVEL,
} from '../../constants/accountLevels';
import api from '../../services/api';

const LEVEL_ACTIONS = {
  1: {
    label: 'Verificar email agora',
    description: 'Envie um novo email de confirmação e valide o endereço cadastrado.',
    destination: '/account/edit',
    type: 'resend-email',
  },
  2: {
    label: 'Preencher dados do nível 2',
    description: 'Informe nome, sobrenome, CPF e data de nascimento para concluir o nível 2.',
    destination: '/account/level/2/input',
  },
  3: {
    label: 'Adicionar contato e endereço',
    description: 'Inclua telefone/celular e endereço residencial para avançar.',
    destination: '/account/edit',
  },
  4: {
    label: 'Enviar documentos',
    description: 'Anexe documento de identidade e comprovante de endereço válidos.',
    destination: '/account/edit',
  },
  5: {
    label: 'Enviar selfie de verificação',
    description: 'Finalize o processo com uma selfie para validação biométrica.',
    destination: '/account/edit',
  },
};

const AccountLevelStep = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { step } = useParams();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const toggleSidebar = () => setSidebarOpen((prev) => !prev);
  const [actionStatus, setActionStatus] = useState({ loading: false, success: '', error: '' });

  const stepId = useMemo(() => {
    const parsed = Number(step);
    return Number.isFinite(parsed) ? parsed : NaN;
  }, [step]);

  const currentLevel = useMemo(() => resolveUserLevel(user), [user]);
  const registrationStep = useMemo(() => getRegistrationStep(user), [user]);
  const nextPendingLevel = useMemo(() => getNextPendingLevel(user), [user]);
  const emailVerified = useMemo(() => isEmailVerified(user), [user]);
  const userUuid = useMemo(() => {
    if (!user) return null;
    return (
      user?.uuid ||
      user?.Uuid ||
      user?.UUID ||
      user?.userUuid ||
      user?.UserUuid ||
      user?.user_uuid ||
      null
    );
  }, [user]);

  const levelConfig = useMemo(() => {
    if (!Number.isFinite(stepId)) return null;

    if (stepId === 0) {
      return getLevelById(0);
    }

    if (stepId === nextPendingLevel) {
      return getLevelById(stepId);
    }

    if (stepId === currentLevel) {
      return getLevelById(stepId);
    }

    if (stepId <= registrationStep) {
      return getLevelById(stepId);
    }

    if (nextPendingLevel !== null) {
      return null;
    }

    if (stepId === MAX_ACCOUNT_LEVEL) {
      return getLevelById(stepId);
    }

    return null;
  }, [stepId, nextPendingLevel, currentLevel, registrationStep]);

  if (!Number.isFinite(stepId) || !levelConfig) {
    return (
      <div className="min-h-screen bg-[#f7f7f7] text-slate-700">
        <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-6 text-center">
          <h1 className="text-2xl font-semibold">Acesso não permitido</h1>
          <p className="text-sm text-slate-500 max-w-md">
            Você não possui requisitos para acessar diretamente este nível. Continue seguindo a ordem das etapas
            para avançar.
          </p>
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-full bg-tBlue px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-tBlue2"
            onClick={() =>
              navigate(
                nextPendingLevel !== null && Number.isFinite(nextPendingLevel)
                  ? `/account/level/${nextPendingLevel}`
                  : '/account/level'
              )
            }
          >
            Ir para o próximo nível disponível
            <i className="bi-arrow-right-short text-lg"></i>
          </button>
        </div>
      </div>
    );
  }

  const actionConfig = LEVEL_ACTIONS[levelConfig.id];

  const isCompleted = registrationStep >= levelConfig.id && emailVerified;
  const isCurrentLevel = currentLevel === levelConfig.id;
  const isNextPending = nextPendingLevel === levelConfig.id;

  const statusMessage = (() => {
    if (isCompleted && levelConfig.id !== 0) {
      return {
        title: 'Nível concluído',
        description:
          'Você já completou este nível. Verifique abaixo os requisitos cumpridos ou avance para os próximos passos.',
        variant: 'success',
      };
    }

    if (isNextPending || (levelConfig.id === 1 && !emailVerified)) {
      return {
        title: 'Próximo passo necessário',
        description: 'Complete os requisitos listados abaixo para subir de nível.',
        variant: 'warning',
      };
    }

    if (nextPendingLevel && levelConfig.id > nextPendingLevel) {
      return {
        title: `Complete primeiro o nível ${nextPendingLevel}`,
        description:
          'Finalize o próximo nível pendente antes de acessar os requisitos desta etapa.',
        variant: 'info',
      };
    }

    return {
      title: 'Informações do nível',
      description: 'Confira os requisitos e benefícios associados a este nível.',
      variant: 'info',
    };
  })();

  const bannerStyles = {
    success: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    warning: 'border-amber-200 bg-amber-50 text-amber-800',
    info: 'border-slate-200 bg-slate-50 text-slate-600',
  }[statusMessage.variant];

  const handlePrimaryAction = async () => {
    setActionStatus({ loading: false, success: '', error: '' });

    if (isNextPending && actionConfig?.type === 'resend-email') {
      if (!userUuid) {
        setActionStatus({ loading: false, success: '', error: 'Não foi possível identificar o usuário.' });
        return;
      }
      try {
        setActionStatus({ loading: true, success: '', error: '' });
        await api.post('/account/verify-email', { userUuid });
        setActionStatus({
          loading: false,
          success: 'Enviamos um novo email de confirmação. Confira sua caixa de entrada (e o spam, se necessário).',
          error: '',
        });
      } catch (error) {
        setActionStatus({
          loading: false,
          success: '',
          error:
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message ||
            'Não foi possível reenviar o email. Tente novamente em instantes.',
        });
      }
      return;
    }

    if (isNextPending && actionConfig?.destination) {
      navigate(actionConfig.destination);
      return;
    }

    if (nextPendingLevel && levelConfig.id !== nextPendingLevel) {
      navigate(`/account/level/${nextPendingLevel}`);
      return;
    }

    navigate('/account/level');
  };

  const primaryButtonLabel = () => {
    if (isNextPending && actionConfig?.type === 'resend-email') {
      return actionStatus.loading ? 'Enviando...' : 'Verificar email agora';
    }

    if (isNextPending && actionConfig) {
      return actionConfig.label;
    }

    if (nextPendingLevel && levelConfig.id > nextPendingLevel) {
      return `Ir para nível ${nextPendingLevel}`;
    }

    if (isCompleted) {
      return 'Voltar para níveis';
    }

    return 'Voltar para níveis';
  };

  return (
    <div className="min-h-screen bg-[#f7f7f7] text-slate-700">
      <div className="min-h-screen flex flex-col lg:flex-row">
        <div className="lg:hidden">
          <div className="flex items-center justify-between px-6 py-4 bg-white shadow-sm">
            <button
              type="button"
              className="text-2xl text-slate-700"
              onClick={toggleSidebar}
              aria-label="Abrir menu"
            >
              <i className="bi-list"></i>
            </button>
            <img src="/img/branded/logob.png" alt="Stackfy" className="h-8" />
            <div className="w-7" />
          </div>
        </div>

        <InternalSidebar active="account-section" open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

        {sidebarOpen && (
          <button
            type="button"
            className="fixed inset-0 bg-slate-900/50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
            aria-label="Fechar menu"
          ></button>
        )}

        <main className="flex-1 px-6 py-8 lg:px-10 lg:py-12">
          <EmailVerificationBanner />

          <header className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <button
              type="button"
              onClick={() => navigate('/account/level')}
              className="text-sm font-semibold flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
            >
              <i className="bi-arrow-left-short text-xl"></i> Voltar para níveis
            </button>
            <InternalTopMenu onLogout={logout} />
          </header>

          <section className="max-w-4xl mx-auto space-y-8">
            <div className={`rounded-3xl border px-6 py-6 shadow-sm ${bannerStyles}`}>
              <h1 className="text-xl font-semibold">{levelConfig.title}</h1>
              <p className="mt-2 text-sm">{statusMessage.description}</p>
            </div>

            <div className="rounded-3xl border border-slate-200 bg-white px-6 py-6 shadow-sm space-y-6">
              <div>
                <h2 className="text-base font-semibold text-slate-900">Requisitos necessários</h2>
                <p className="mt-2 text-xs text-slate-500">Conclua todos os itens abaixo para avançar.</p>
                <ul className="mt-4 space-y-3 text-sm text-slate-600">
                  {levelConfig.requirements.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <i className="bi-check2-circle text-lg text-emerald-500"></i>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h2 className="text-base font-semibold text-slate-900">Benefícios liberados</h2>
                <p className="mt-2 text-xs text-slate-500">Ao concluir este nível, você habilita:</p>
                <ul className="mt-4 space-y-3 text-sm text-slate-600">
                  {levelConfig.limits.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <i className="bi-lightning-charge-fill text-lg text-tBlue"></i>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {actionConfig ? (
                <div className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-4 text-sm text-slate-600">
                  <p className="font-semibold text-slate-800">O que fazer agora?</p>
                  <p className="mt-1 text-xs text-slate-500">{actionConfig.description}</p>
                </div>
              ) : null}

              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-xs text-slate-500">
                  {isCompleted
                    ? 'Este nível já foi concluído. Revise as informações ou avance para os próximos níveis.'
                    : 'Finalize os requisitos para liberar automaticamente os benefícios.'}
                </div>
                <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={handlePrimaryAction}
                    className={`inline-flex items-center justify-center gap-2 rounded-full px-4 py-2 text-xs font-semibold transition-colors ${
                      isNextPending && actionConfig
                        ? 'bg-tBlue text-white hover:bg-tBlue2 disabled:opacity-70'
                        : 'bg-slate-900 text-white hover:bg-slate-800'
                    }`}
                    disabled={actionStatus.loading}
                  >
                    {primaryButtonLabel()}
                    <i className="bi-arrow-right-short text-lg"></i>
                  </button>
                  {!isCompleted && actionConfig && actionConfig.destination && stepId !== 2 ? (
                    <button
                      type="button"
                      onClick={() => navigate(actionConfig.destination)}
                      className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-100"
                    >
                      Abrir página de cadastro
                      <i className="bi-box-arrow-up-right text-lg"></i>
                    </button>
                  ) : null}
                </div>
              </div>

              {actionStatus.success ? (
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-xs font-semibold text-emerald-700">
                  {actionStatus.success}
                </div>
              ) : null}

              {actionStatus.error ? (
                <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-xs font-semibold text-red-600">
                  {actionStatus.error}
                </div>
              ) : null}
            </div>
          </section>
        </main>
      </div>
    </div>
  );
};

export default AccountLevelStep;
